import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, Package, Globe, Lock, ArrowRight, WhatsappLogo, Envelope } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransportLuxePage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service_type: "transport_luxe", message: "", preferred_contact: "email",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("OK");
        setFormData({ name: "", email: "", phone: "", service_type: "transport_luxe", message: "", preferred_contact: "email" });
      }
    } catch (error) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const features = [
    { icon: Globe, title: t("transport_luxe.features_1_title"), description: t("transport_luxe.features_1_desc") },
    { icon: Lock, title: t("transport_luxe.features_2_title"), description: t("transport_luxe.features_2_desc") },
    { icon: Package, title: t("transport_luxe.features_3_title"), description: t("transport_luxe.features_3_desc") },
  ];

  const services = [
    t("transport_luxe.service_1"), t("transport_luxe.service_2"), t("transport_luxe.service_3"),
    t("transport_luxe.service_4"), t("transport_luxe.service_5"), t("transport_luxe.service_6"), t("transport_luxe.service_7"),
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="transport-luxe-page">
      <SEO title="Transport de Luxe — Biens Précieux" path="/services/transport-luxe" description="Transport sécurisé de biens précieux à Paris par ALTURA. Livraison en main propre, discrétion absolue, suivi en temps réel. Bijoux, œuvres d'art, documents confidentiels." />
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/545976fc-774f-4132-bae0-c645581525da/images/8180aa7e9cf4585697278695a2fe310fad0cc9196a720372f4bef388ec9e7eb1.png" alt="Transport de luxe ALTURA — œuvres d'art, haute couture, objets de valeur" className="w-full h-full object-cover" />
          <div className="hero-overlay-side absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-md hero-text-backdrop">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("transport_luxe.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none">
              {t("transport_luxe.hero_title_1")}<br /><span className="italic">{t("transport_luxe.hero_title_2")}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <feature.icon size={40} weight="thin" className="text-[#1A1A1A] mx-auto mb-6" />
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-4">{feature.title}</h3>
                <p className="text-[#1A1A1A]/70 font-light text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-8">{t("transport_luxe.services_title")}</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{t("transport_luxe.services_desc")}</p>
              <ul className="space-y-4 mb-12">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <Check size={20} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#1A1A1A]/80 font-light">{service}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 mt-8">
                <Globe size={20} weight="thin" className="text-[#1A1A1A]" />
                <div>
                  <p className="text-[#1A1A1A] text-sm font-medium">Paris</p>
                  <p className="text-[#1A1A1A]/50 text-xs">Londres / Madrid</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("transport_luxe.quote_title")}</h2>
                <p className="text-[#1A1A1A]/70 text-sm mb-8">{t("transport_luxe.quote_desc")}</p>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="transport-luxe-form">
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("transport_luxe.name_label")} *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("transport_luxe.name_placeholder")} data-testid="input-name" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("transport_luxe.email_label")} *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("transport_luxe.email_placeholder")} data-testid="input-email" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("transport_luxe.phone_label")}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder={t("transport_luxe.phone_placeholder")} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("transport_luxe.contact_pref")}</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="email" checked={formData.preferred_contact === "email"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <Envelope size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.email")}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="whatsapp" checked={formData.preferred_contact === "whatsapp"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <WhatsappLogo size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.whatsapp")}</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("transport_luxe.message_label")} *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-luxury w-full bg-transparent resize-none" placeholder={t("transport_luxe.message_placeholder")} data-testid="input-message" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="submit-contact">
                    {loading ? t("transport_luxe.submitting") : t("transport_luxe.submit")}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TransportLuxePage;
